import { useState } from 'preact/hooks';
import { isVoided, type Entry, type Paisa } from '@khata/core';
import { Field, TextInput } from '../components/index.js';
import { fromApi, useSave } from '../lib/form.js';
import { dShort, num, qty, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, state, submit, today, userName } from '../lib/khata.js';
import {
  accountName,
  adjustReason,
  itemName,
  partyName,
  returnReason,
  typeLabel,
} from '../lib/labels.js';
import { go } from '../lib/router.js';
import { closeSheet } from '../lib/ui.js';
import { FormErrorLine, Sheet } from './Sheet.js';

function Detail(props: { rows: Array<[string, string]> }) {
  return (
    <dl class="detail">
      {props.rows.map(([k, v]) => (
        <>
          <dt key={k + 'k'}>{k}</dt>
          <dd key={k + 'v'}>{v}</dd>
        </>
      ))}
    </dl>
  );
}

export function EntrySheet(props: { id: string }) {
  const s = state.value;
  const e = entries.value.find((x) => x.id === props.id);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [error, busy, save] = useSave();
  if (!e) return <Sheet title={t('type_void')}>{t('entry_notFound')}</Sheet>;
  const voided = isVoided(s, e.id);
  const voidEntry = voided
    ? entries.value.find((x) => x.type === 'void' && x.targetId === e.id)
    : undefined;

  const title =
    e.type === 'sale' || e.type === 'purchase'
      ? `${typeLabel(e.type)} #${num(e.no)}`
      : typeLabel(e.type);
  const rows: Array<[string, string]> = [[t('date'), dShort(e.date)]];
  const body = rowsFor(e, rows);
  if (e.by) rows.push([t('entry_by'), userName(e.by) || t('entry_unknownUser')]);

  const onVoid = () =>
    save(async () => {
      const r = await submit('void', { date: today.value, targetId: e.id, reason });
      if (!r.ok) return fromApi(r.error);
      return t('entry_voidedToast', { title });
    });

  return (
    <Sheet title={title}>
      {voided && (
        <div class="banner">
          {t('entry_voidedBanner', {
            when:
              voidEntry && voidEntry.type === 'void'
                ? t('entry_on', { date: dShort(voidEntry.date) }) +
                  (voidEntry.reason ? ' · ' + voidEntry.reason : '')
                : '',
          })}
        </div>
      )}
      <Detail rows={rows} />
      {body}
      {e.note && (
        <div class="small muted">
          {t('entry_note')}: {e.note}
        </div>
      )}
      {e.type === 'sale' && (
        <div>
          <button
            type="button"
            class="btn btn-secondary"
            onClick={() => (closeSheet(), go('memo', e.id))}
          >
            {t('entry_printMemo')}
          </button>
        </div>
      )}
      {e.type !== 'void' && !voided && (
        <div style={{ marginTop: 12 }}>
          {!confirm ? (
            <button type="button" class="btn btn-ghost btn-danger" onClick={() => setConfirm(true)}>
              {t('entry_void')}
            </button>
          ) : (
            <>
              <div class="help" style={{ marginBottom: 8 }}>
                {t('entry_voidHelp')}
              </div>
              <Field label={t('entry_voidReason')}>
                <TextInput
                  value={reason}
                  onInput={setReason}
                  placeholder={t('entry_voidReasonPlaceholder')}
                  autoFocus
                />
              </Field>
              <div class="actions" style={{ margin: '10px 0 0' }}>
                <button
                  type="button"
                  class="btn btn-secondary btn-danger"
                  disabled={busy}
                  onClick={onVoid}
                >
                  {busy ? t('entry_voiding') : t('entry_confirmVoid')}
                </button>
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => (setConfirm(false), closeSheet())}
                >
                  {t('entry_keep')}
                </button>
              </div>
            </>
          )}
          <FormErrorLine error={error} />
        </div>
      )}
    </Sheet>
  );
}

function rowsFor(e: Entry, rows: Array<[string, string]>) {
  const m = master.value;
  switch (e.type) {
    case 'sale':
    case 'purchase': {
      const due = (e.total - e.paid) as Paisa;
      rows.push([
        e.type === 'sale' ? t('customer') : t('vendor'),
        partyName(m, e.partyId, e.type === 'sale' ? t('walkIn') : t('noVendor')),
      ]);
      rows.push(
        [t('entry_total'), taka(e.total)],
        [t('entry_paid'), `${taka(e.paid)} · ${accountName(m, e.accountId)}`],
      );
      if (due > 0)
        rows.push([e.type === 'sale' ? t('entry_bokeya') : t('entry_stillOwed'), taka(due)]);
      return (
        <table class="table">
          <thead>
            <tr>
              <th>{t('entry_item')}</th>
              <th style={{ textAlign: 'right' }}>{t('sheet_qty')}</th>
              <th style={{ textAlign: 'right' }}>
                {e.type === 'sale' ? t('sheet_price') : t('sheet_cost')}
              </th>
              <th style={{ textAlign: 'right' }}>{t('entry_amount')}</th>
            </tr>
          </thead>
          <tbody>
            {e.lines.map((l) => (
              <tr key={l.itemId}>
                <td>{itemName(m, l.itemId)}</td>
                <td style={{ textAlign: 'right' }}>{qty(l.qty)}</td>
                <td style={{ textAlign: 'right' }}>
                  {taka('unitPrice' in l ? l.unitPrice : l.unitCost)}
                </td>
                <td style={{ textAlign: 'right' }}>{taka(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    case 'payment':
      rows.push(
        [e.direction === 'in' ? t('entry_from') : t('entry_to'), partyName(m, e.partyId)],
        [t('entry_amount'), taka(e.amount)],
        [t('entry_via'), accountName(m, e.accountId)],
      );
      return null;
    case 'return':
      rows.push(
        [t('entry_kind'), e.kind === 'sale' ? t('return_customerReturned') : t('return_weReturn')],
        [
          t('entry_party'),
          partyName(m, e.partyId, e.kind === 'sale' ? t('walkIn') : t('noVendor')),
        ],
        [t('entry_item'), `${itemName(m, e.itemId)} × ${qty(e.qty)}`],
        [t('entry_amount'), taka(e.amount)],
        [t('entry_reason'), returnReason(e.reason)],
        [
          e.kind === 'sale' ? t('entry_wentTo') : t('entry_takenFrom'),
          e.bin === 'defective' ? t('defectiveBin') : t('stock'),
        ],
      );
      if (e.partyId === null) rows.push([t('entry_refundVia'), accountName(m, e.accountId)]);
      return null;
    case 'expense':
      rows.push(
        [t('entry_for'), e.purpose],
        [t('entry_amount'), taka(e.amount)],
        [t('entry_from'), accountName(m, e.accountId)],
      );
      return null;
    case 'drawing':
      rows.push(
        [t('entry_who'), e.who],
        [t('entry_amount'), taka(e.amount)],
        [t('entry_from'), accountName(m, e.accountId)],
      );
      return null;
    case 'transfer':
      rows.push(
        [t('entry_from'), accountName(m, e.fromAccountId)],
        [t('entry_to'), accountName(m, e.toAccountId)],
        [t('entry_amount'), taka(e.amount)],
      );
      return null;
    case 'stock_adjust':
      rows.push(
        [t('entry_item'), itemName(m, e.itemId)],
        [
          t('entry_change'),
          `${e.qty > 0 ? '+' : '−'}${qty(Math.abs(e.qty) as never)} ${t('entry_in')} ${e.bin === 'defective' ? t('defectiveBin') : t('stock')}`,
        ],
        [t('entry_reason'), adjustReason(e.reason)],
      );
      if (e.unitCost !== null) rows.push([t('entry_unitCost'), taka(e.unitCost)]);
      return null;
    case 'account_opening':
      rows.push(
        [t('entry_account'), accountName(m, e.accountId)],
        [t('entry_openingBalance'), taka(e.amount)],
      );
      return null;
    case 'party_opening':
      rows.push(
        [t('entry_party'), partyName(m, e.partyId)],
        [t('entry_openingBalance'), taka(e.amount)],
      );
      return null;
    case 'void':
      rows.push([t('entry_voidedEntry'), e.targetId], [t('entry_reason'), e.reason || '—']);
      return null;
  }
}
